import React, { useState, useEffect, useCallback } from 'react';
import { submitRequest, createRequestFromFiles, getUploadedFiles, getFileAwards, uploadFile, deleteFile } from '../services/api';

function ExcelFormatGuide() {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginBottom: 12, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls="format-guide-body"
        style={{
          width: '100%', padding: '8px 12px', background: 'var(--bg-secondary)', border: 'none',
          color: 'var(--text-primary)', cursor: 'pointer', display: 'flex', alignItems: 'center',
          gap: 8, fontSize: 12, fontWeight: 600,
        }}
      >
        <span aria-hidden="true" style={{ fontSize: 10 }}>{open ? '\u25BC' : '\u25B6'}</span>
        Excel File Format Reference
      </button>
      {open && (
        <div id="format-guide-body" style={{ padding: '10px 14px', fontSize: 11, lineHeight: 1.6 }}>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontWeight: 700, color: 'var(--accent-hover)', marginBottom: 4 }}>Expenditure File Columns</div>
            <table className="data-table compact" style={{ fontSize: 11 }}>
              <thead>
                <tr><th>Column Name</th><th>Required</th><th>Example</th></tr>
              </thead>
              <tbody>
                <tr><td>Exp ID</td><td>Auto-generated if missing</td><td>EXP-001</td></tr>
                <tr><td>Amount</td><td></td><td>5200.00</td></tr>
                <tr><td>Category</td><td style={{color:'var(--green)'}}>Key field</td><td>Laboratory Supplies</td></tr>
                <tr><td>Type</td><td></td><td>Scientific Lab Consumables</td></tr>
                <tr><td>Description</td><td style={{color:'var(--green)'}}>Key field</td><td>Purchase of PCR reagents</td></tr>
                <tr><td>Organization</td><td></td><td>Department of Biology</td></tr>
                <tr><td>Project Name</td><td></td><td>Genomics Research Initiative</td></tr>
                <tr><td>Task Name</td><td></td><td>Lab Operations</td></tr>
                <tr><td>Award Name</td><td></td><td>Chen Research Fund</td></tr>
                <tr><td>Project Owner</td><td style={{color:'var(--green)'}}>Key field</td><td>Chen, Wei</td></tr>
                <tr><td>Task Owner</td><td></td><td>Chen, Wei</td></tr>
                <tr><td>Award Owner</td><td></td><td>Chen, Wei</td></tr>
              </tbody>
            </table>
          </div>
          <div>
            <div style={{ fontWeight: 700, color: 'var(--accent-hover)', marginBottom: 4 }}>Award / Candidate File Columns</div>
            <table className="data-table compact" style={{ fontSize: 11 }}>
              <thead>
                <tr><th>Column Name</th><th>Required</th><th>Example</th></tr>
              </thead>
              <tbody>
                <tr><td>Award Number</td><td style={{color:'var(--green)'}}>Required</td><td>AWD-1001</td></tr>
                <tr><td>Award Name</td><td style={{color:'var(--green)'}}>Key field</td><td>Chen Genomics Fund</td></tr>
                <tr><td>Organization</td><td></td><td>School of Engineering</td></tr>
                <tr><td>Start Date</td><td></td><td>2018-03-15</td></tr>
                <tr><td>Purpose</td><td style={{color:'var(--green)'}}>Key field</td><td>RESEARCH_SUPPORT</td></tr>
                <tr><td>Fund Authorization</td><td style={{color:'var(--green)'}}>Key field</td><td>Lab supplies and equipment</td></tr>
                <tr><td>Control Level</td><td></td><td>Faculty Specific</td></tr>
                <tr><td>Restriction Level</td><td style={{color:'var(--green)'}}>Key field</td><td>Highly Restricted</td></tr>
                <tr><td>Role</td><td></td><td>PRINCIPAL_OWNER</td></tr>
                <tr><td>Employee Name</td><td></td><td>Chen, Wei</td></tr>
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 8, color: 'var(--text-muted)', fontSize: 10 }}>
            Columns marked <span style={{color:'var(--green)'}}>Key field</span> are used for scoring. Other column name variants (e.g. "Award Number", "Awd Num", "award_number") are auto-detected.
            Sample files available in <code>docs/sample-data/</code>.
          </div>
        </div>
      )}
    </div>
  );
}

const SAMPLE_PAYLOAD = {
  "request_id": "",
  "top_n": 5,
  "matching_mode": "vector",
  "expenditures": [
    {
      "exp_id": "exp1",
      "amt": 500.0,
      "awd_num": "AWD-000101",
      "exp_cat": "Laboratory Supplies",
      "exp_type": "Scientific Lab Consumables",
      "exp_txn_long_desc": "Purchase of laboratory chemicals and materials",
      "task_org_name": "Department of Aeronautics and Astronautics",
      "proj_name": "Advanced Aerodynamics Research Project",
      "awd_name": "Kroo Aeronautics Research Fund",
      "proj_owner": "Kroo, Ilan"
    }
  ],
  "candidates": [
    {
      "awd_num": "AWD-000101",
      "awd_org": "School of Engineering",
      "awd_start": "2015-01-01",
      "awd_name": "Ilan Kroo Aeronautics Research Support Fund",
      "awd_purpose": "RESEARCH_SUPPORT",
      "awd_fund_auth": "Funds may be used for research supplies, laboratory equipment, and graduate research support.",
      "awd_ctrl": "Faculty Specific",
      "awd_restrict": "Highly Restricted",
      "awd_role": "PRINCIPAL_OWNER",
      "awd_role_emp": "Kroo, Ilan"
    }
  ]
};

function DeleteConfirmBanner({ fileName, onConfirm, onCancel }) {
  const [deleting, setDeleting] = React.useState(false);
  const handleConfirm = async () => {
    setDeleting(true);
    await onConfirm();
    setDeleting(false);
  };
  return (
    <div style={{
      marginTop: 8, padding: '10px 14px', borderRadius: 8,
      background: 'var(--red-bg)', border: '1px solid var(--red)',
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    }}>
      <span style={{ fontSize: 12, color: 'var(--red)', flex: 1 }}>
        <strong>Permanently delete</strong> &ldquo;{fileName}&rdquo; and all its stored data? This cannot be undone.
      </span>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <button
          onClick={onCancel}
          disabled={deleting}
          style={{
            padding: '5px 14px', borderRadius: 6, border: '1px solid var(--border)',
            background: 'var(--bg-card)', color: 'var(--text-secondary)',
            cursor: 'pointer', fontSize: 12, fontWeight: 600,
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleConfirm}
          disabled={deleting}
          style={{
            padding: '5px 14px', borderRadius: 6, border: 'none',
            background: 'var(--red)', color: '#fff',
            cursor: deleting ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700,
            opacity: deleting ? 0.7 : 1,
          }}
        >
          {deleting ? 'Deleting…' : 'Yes, Delete'}
        </button>
      </div>
    </div>
  );
}

export default function SubmitRequest({ onSubmitted }) {
  const [inputMode, setInputMode] = useState('file');
  const [json, setJson] = useState(JSON.stringify(SAMPLE_PAYLOAD, null, 2));
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Existing files
  const [existingExpFiles, setExistingExpFiles] = useState([]);
  const [existingAwardFiles, setExistingAwardFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);

  // Selected expenditure file
  const [selectedExpFileId, setSelectedExpFileId] = useState('');
  const [selectedExpFile, setSelectedExpFile] = useState(null);
  const [uploadingExp, setUploadingExp] = useState(false);

  // Award source
  const [selectedAwardFileId, setSelectedAwardFileId] = useState('');
  const [awardFileAwards, setAwardFileAwards] = useState([]);
  const [selectedAwardNums, setSelectedAwardNums] = useState(new Set());
  const [manualAwardNumbers, setManualAwardNumbers] = useState('');
  const [loadingAwards, setLoadingAwards] = useState(false);
  const [awardSearch, setAwardSearch] = useState('');
  // true when every picker award is checked (regardless of manual field or truncation)
  const allPickerSelected = awardFileAwards.length > 0 && selectedAwardNums.size === awardFileAwards.length;
  // "select all" semantics for the checkbox label
  const selectAllAwards = allPickerSelected;

  // Options
  const [matchingModes, setMatchingModes] = useState(['vector']);
  const [topN, setTopN] = useState(5);

  // Inline delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { fileId, fileType, fileName }

  // Load existing files on mount
  const loadFiles = useCallback(async () => {
    setLoadingFiles(true);
    try {
      const [expFiles, awdFiles] = await Promise.all([
        getUploadedFiles('expenditures'),
        getUploadedFiles('awards'),
      ]);
      setExistingExpFiles(expFiles);
      setExistingAwardFiles(awdFiles);
    } catch (err) {
      console.error('Failed to load files:', err);
    } finally {
      setLoadingFiles(false);
    }
  }, []);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  // Load awards for the picker
  const fetchAwards = useCallback((fileId) => {
    if (!fileId) {
      setAwardFileAwards([]);
      setSelectedAwardNums(new Set());
      return;
    }
    setLoadingAwards(true);
    getFileAwards(fileId)
      .then(data => {
        const awards = data.awards || [];
        setAwardFileAwards(awards);
        setSelectedAwardNums(new Set(awards.map(a => String(a.award_number || '').trim())));
      })
      .catch(err => console.error('Failed to load awards:', err))
      .finally(() => setLoadingAwards(false));
  }, []);

  useEffect(() => {
    setAwardSearch('');
    fetchAwards(selectedAwardFileId);
  }, [selectedAwardFileId, fetchAwards]);

  // Auto-select awards from file when comma-separated award numbers are typed
  useEffect(() => {
    if (!awardFileAwards.length || !manualAwardNumbers.trim()) return;
    const typed = manualAwardNumbers.split(',').map(n => n.trim()).filter(Boolean);
    // Case-insensitive lookup: map lowercase → original award number from file
    const lowerToOriginal = new Map(
      awardFileAwards.map(a => {
        const orig = String(a.award_number || '').trim();
        return [orig.toLowerCase(), orig];
      })
    );
    const matches = typed
      .map(n => lowerToOriginal.get(n.toLowerCase()))
      .filter(Boolean);
    if (matches.length === 0) return;
    setSelectedAwardNums(prev => {
      const next = new Set(prev);
      matches.forEach(n => next.add(n));
      return next;
    });
    // selectAllAwards is now derived — no setState needed; keep text in box
  }, [manualAwardNumbers, awardFileAwards]); // eslint-disable-line

  // Delete an uploaded file (removes MongoDB doc + GridFS data)
  const handleDeleteFile = async (fileId, fileType) => {
    try {
      await deleteFile(fileId);
      setDeleteConfirm(null);
      if (fileType === 'expenditures') {
        if (selectedExpFileId === fileId) {
          setSelectedExpFileId('');
          setSelectedExpFile(null);
        }
        setExistingExpFiles(prev => prev.filter(f => f.file_id !== fileId));
      } else {
        if (selectedAwardFileId === fileId) {
          setSelectedAwardFileId('');
          setAwardFileAwards([]);
          setSelectedAwardNums(new Set());
        }
        setExistingAwardFiles(prev => prev.filter(f => f.file_id !== fileId));
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setDeleteConfirm(null);
    }
  };

  // Handle new expenditure file upload
  const handleExpUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingExp(true);
    try {
      const data = await uploadFile(file, 'expenditures');
      setSelectedExpFile(data);
      setSelectedExpFileId(data.file_id);
      loadFiles(); // refresh dropdown
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setUploadingExp(false);
    }
  };

  // Handle new award file upload
  const handleAwardUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoadingAwards(true);
    try {
      const data = await uploadFile(file, 'awards');
      setSelectedAwardFileId(data.file_id);
      loadFiles(); // refresh dropdown
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoadingAwards(false);
    }
  };

  // Handle expenditure dropdown change
  const handleExpSelect = (fileId) => {
    setSelectedExpFileId(fileId);
    const found = existingExpFiles.find(f => f.file_id === fileId);
    setSelectedExpFile(found || null);
  };

  // Toggle individual award selection
  const toggleAward = (awardNum) => {
    setSelectedAwardNums(prev => {
      const next = new Set(prev);
      if (next.has(awardNum)) next.delete(awardNum); else next.add(awardNum);
      return next;
    });
  };

  // Select/deselect all awards
  const handleSelectAll = () => {
    if (selectAllAwards) {
      setSelectedAwardNums(new Set());
    } else {
      setSelectedAwardNums(new Set(awardFileAwards.map(a => String(a.award_number || '').trim())));
    }
  };

  // Parse the manual text field into a clean array
  const getManualEntries = () =>
    manualAwardNumbers.split(',').map(n => n.trim()).filter(Boolean);

  // Build the final award numbers list (for validation / count display only)
  const getFinalAwardNumbers = () => {
    const nums = new Set(selectedAwardNums);
    getManualEntries().forEach(n => nums.add(n));
    return [...nums];
  };

  const toggleMode = (mode) => {
    setMatchingModes(prev =>
      prev.includes(mode) ? prev.filter(m => m !== mode) : [...prev, mode]
    );
  };

  const handleFileSubmit = async () => {
    setError(null);
    setResult(null);

    if (!selectedExpFileId) {
      setError('Please select or upload an expenditure file');
      return;
    }
    const finalAwards = getFinalAwardNumbers();
    if (finalAwards.length === 0) {
      setError('Please select awards or enter award numbers');
      return;
    }
    if (matchingModes.length === 0) {
      setError('Please select at least one matching mode');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        expenditure_file_id: selectedExpFileId,
        matching_modes: matchingModes,
        top_n: topN,
      };

      const manualEntries = getManualEntries();

      if (selectedAwardFileId && allPickerSelected && manualEntries.length === 0) {
        // All picker awards selected, nothing typed → use the full file (no filter)
        payload.award_file_ids = [selectedAwardFileId];
      } else if (selectedAwardFileId && allPickerSelected && manualEntries.length > 0) {
        // All picker awards selected + manual entries typed.
        // Critically: do NOT send all picker award numbers — for large files that payload
        // would be hundreds of kilobytes. Instead send just the typed entries as the filter;
        // the server will look them up in the file and use only those rows as candidates.
        payload.award_file_ids = [selectedAwardFileId];
        payload.award_numbers  = manualEntries;
      } else {
        // Partial picker selection — send only the checked subset + manual entries
        const combined = [...new Set([...selectedAwardNums, ...manualEntries])];
        payload.award_numbers = combined;
        if (selectedAwardFileId) payload.award_file_ids = [selectedAwardFileId];
      }

      const res = await createRequestFromFiles(payload);
      setResult(res);
      const firstId = res.requests?.[0]?.request_id || res.request_id;
      if (onSubmitted && firstId) onSubmitted(firstId);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleJsonSubmit = async () => {
    setError(null);
    setResult(null);
    try {
      const payload = JSON.parse(json);
      if (!payload.request_id) payload.request_id = crypto.randomUUID();
      setSubmitting(true);
      const res = await submitRequest(payload);
      setResult(res);
      if (onSubmitted) onSubmitted(res.request_id);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Filter awards by search
  const filteredAwards = awardSearch
    ? awardFileAwards.filter(a =>
        a.award_number?.toLowerCase().includes(awardSearch.toLowerCase()) ||
        a.award_long_name?.toLowerCase().includes(awardSearch.toLowerCase())
      )
    : awardFileAwards;

  const finalAwardCount = getFinalAwardNumbers().length;

  return (
    <div>
      <div className="section-title" style={{ marginBottom: 16 }}>Submit Match Request</div>

      {/* Input mode tabs */}
      <div className="mode-filter-tabs" style={{ marginBottom: 16 }}>
        <button className={`mode-filter-tab ${inputMode === 'file' ? 'active' : ''}`} onClick={() => setInputMode('file')}>
          File Upload
        </button>
        <button className={`mode-filter-tab ${inputMode === 'json' ? 'active' : ''}`} onClick={() => setInputMode('json')}>
          JSON Input
        </button>
      </div>

      {inputMode === 'file' ? (
        <div className="file-submit-form">
          <ExcelFormatGuide />
          {/* Expenditure File Section */}
          <div className="submit-section">
            <div className="submit-section-label">1. Expenditure File</div>
            <div className="submit-section-row">
              <select
                className="file-select"
                value={selectedExpFileId}
                onChange={e => handleExpSelect(e.target.value)}
              >
                <option value="">-- Select existing file --</option>
                {existingExpFiles.map(f => (
                  <option key={f.file_id} value={f.file_id}>
                    {f.original_name} ({f.row_count} rows) - {new Date(f.createdAt).toLocaleDateString()}
                  </option>
                ))}
              </select>
              {selectedExpFileId && !deleteConfirm && (
                <button
                  title="Delete this file from database"
                  onClick={() => setDeleteConfirm({
                    fileId: selectedExpFileId,
                    fileType: 'expenditures',
                    fileName: existingExpFiles.find(f => f.file_id === selectedExpFileId)?.original_name || selectedExpFileId,
                  })}
                  style={{
                    background: 'none', border: '1px solid var(--red)', borderRadius: 6,
                    color: 'var(--red)', cursor: 'pointer', padding: '4px 10px', fontSize: 12,
                    fontWeight: 600, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4,
                  }}
                >
                  &#128465; Delete
                </button>
              )}
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>or</span>
              <label className="upload-inline-btn">
                {uploadingExp ? 'Uploading...' : 'Upload New'}
                <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleExpUpload} disabled={uploadingExp} />
              </label>
              {loadingFiles && <div className="spinner" style={{ width: 16, height: 16 }} />}
            </div>
            {selectedExpFile && !deleteConfirm && (
              <div style={{ fontSize: 11, color: 'var(--green)', marginTop: 4 }}>
                {selectedExpFile.original_name || 'Selected'} — {selectedExpFile.row_count} expenditures
              </div>
            )}
            {selectedExpFileId && !selectedExpFile && !deleteConfirm && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                {existingExpFiles.find(f => f.file_id === selectedExpFileId)?.row_count || '?'} expenditures
              </div>
            )}
            {deleteConfirm && deleteConfirm.fileType === 'expenditures' && (
              <DeleteConfirmBanner
                fileName={deleteConfirm.fileName}
                onConfirm={() => handleDeleteFile(deleteConfirm.fileId, deleteConfirm.fileType)}
                onCancel={() => setDeleteConfirm(null)}
              />
            )}
          </div>

          {/* Awards Section */}
          <div className="submit-section">
            <div className="submit-section-label">2. Award Candidates</div>

            {/* Award file dropdown */}
            <div className="submit-section-row" style={{ marginBottom: 8 }}>
              <select
                className="file-select"
                value={selectedAwardFileId}
                onChange={e => setSelectedAwardFileId(e.target.value)}
              >
                <option value="">-- Select award file (optional) --</option>
                {existingAwardFiles.map(f => (
                  <option key={f.file_id} value={f.file_id}>
                    {f.original_name} ({f.row_count} rows) - {new Date(f.createdAt).toLocaleDateString()}
                  </option>
                ))}
              </select>
              {selectedAwardFileId && !deleteConfirm && (
                <button
                  title="Delete this file from database"
                  onClick={() => setDeleteConfirm({
                    fileId: selectedAwardFileId,
                    fileType: 'awards',
                    fileName: existingAwardFiles.find(f => f.file_id === selectedAwardFileId)?.original_name || selectedAwardFileId,
                  })}
                  style={{
                    background: 'none', border: '1px solid var(--red)', borderRadius: 6,
                    color: 'var(--red)', cursor: 'pointer', padding: '4px 10px', fontSize: 12,
                    fontWeight: 600, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4,
                  }}
                >
                  &#128465; Delete
                </button>
              )}
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>or</span>
              <label className="upload-inline-btn">
                Upload New
                <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleAwardUpload} />
              </label>
            </div>

            {deleteConfirm && deleteConfirm.fileType === 'awards' && (
              <DeleteConfirmBanner
                fileName={deleteConfirm.fileName}
                onConfirm={() => handleDeleteFile(deleteConfirm.fileId, deleteConfirm.fileType)}
                onCancel={() => setDeleteConfirm(null)}
              />
            )}

            {/* Awards from file - multi-select */}
            {loadingAwards && <div className="processing-indicator" style={{ padding: 12 }}><div className="spinner" style={{ width: 16, height: 16 }} /><span style={{ fontSize: 12 }}>Loading awards...</span></div>}

            {awardFileAwards.length > 0 && (
              <div className="award-picker">
                <div className="award-picker-header">
                  <label className="mode-checkbox" style={{ fontSize: 12 }}>
                    <input type="checkbox" checked={selectAllAwards} onChange={handleSelectAll} />
                    <span>All ({awardFileAwards.length})</span>
                  </label>
                  <span style={{ fontSize: 11, color: 'var(--accent-hover)' }}>
                    {selectedAwardNums.size} selected
                  </span>
                  <input
                    type="text"
                    className="award-search"
                    placeholder="Search awards..."
                    value={awardSearch}
                    onChange={e => setAwardSearch(e.target.value)}
                  />
                </div>
                <div className="award-picker-list">
                  {filteredAwards.map(a => (
                    <label key={a.award_number} className="award-picker-item" title={a.award_long_name}>
                      <input
                        type="checkbox"
                        checked={selectedAwardNums.has(String(a.award_number))}
                        onChange={() => toggleAward(String(a.award_number))}
                      />
                      <span className="mono" style={{ fontWeight: 600, fontSize: 11, minWidth: 90 }}>{a.award_number}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a.award_long_name}
                      </span>
                      {a.award_org && <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 'auto', flexShrink: 0 }}>{a.award_org}</span>}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Manual award numbers input */}
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
                {awardFileAwards.length > 0
                  ? 'Additional award numbers (comma-separated):'
                  : 'Enter award numbers (comma-separated):'}
              </div>
              <input
                type="text"
                className="file-select"
                style={{ width: '100%' }}
                placeholder="e.g. AWD-001234, AWD-005678, AWD-009012"
                value={manualAwardNumbers}
                onChange={e => setManualAwardNumbers(e.target.value)}
              />
            </div>
          </div>

          {/* Options */}
          <div className="file-submit-options">
            <div className="file-option-group">
              <label>Matching Mode</label>
              <div className="mode-checkboxes">
                <label className="mode-checkbox">
                  <input type="checkbox" checked={matchingModes.includes('llm')} onChange={() => toggleMode('llm')} />
                  <span className="badge llm-mode">LLM</span>
                </label>
                <label className="mode-checkbox">
                  <input type="checkbox" checked={matchingModes.includes('local-llm')} onChange={() => toggleMode('local-llm')} />
                  <span className="badge local-llm-mode">Local LLM</span>
                </label>
                <label className="mode-checkbox">
                  <input type="checkbox" checked={matchingModes.includes('vector')} onChange={() => toggleMode('vector')} />
                  <span className="badge vector-mode">Vector</span>
                </label>
                <label className="mode-checkbox">
                  <input type="checkbox" checked={matchingModes.includes('hybrid')} onChange={() => toggleMode('hybrid')} />
                  <span className="badge hybrid-mode">Hybrid</span>
                </label>
                <label className="mode-checkbox">
                  <input type="checkbox" checked={matchingModes.includes('vector-llm')} onChange={() => toggleMode('vector-llm')} />
                  <span className="badge vector-llm-mode">Vector+LLM</span>
                </label>
                <label className="mode-checkbox">
                  <input type="checkbox" checked={matchingModes.includes('hybrid-os')} onChange={() => toggleMode('hybrid-os')} />
                  <span className="badge hybrid-os-mode">Hybrid OS</span>
                </label>
                <label className="mode-checkbox">
                  <input type="checkbox" checked={matchingModes.includes('hybrid-os-judge')} onChange={() => toggleMode('hybrid-os-judge')} />
                  <span className="badge hybrid-os-judge-mode">OS Judge</span>
                </label>
              </div>
              {matchingModes.length >= 2 && (
                <span style={{ fontSize: 11, color: 'var(--accent-hover)' }}>{matchingModes.length} modes — creates {matchingModes.length} linked requests</span>
              )}
            </div>
            <div className="file-option-group">
              <label>Top N Results</label>
              <input
                type="number" min={1} max={50} value={topN}
                onChange={e => setTopN(parseInt(e.target.value, 10) || 5)}
                className="top-n-input"
              />
            </div>
          </div>

          {/* Submit */}
          <div style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
            <button
              className="submit-btn"
              onClick={handleFileSubmit}
              disabled={submitting || !selectedExpFileId || finalAwardCount === 0}
            >
              {submitting ? 'Submitting...' : 'Submit Request'}
            </button>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {selectedExpFileId
                ? `${existingExpFiles.find(f => f.file_id === selectedExpFileId)?.row_count || selectedExpFile?.row_count || '?'} expenditures`
                : 'No expenditure file'
              } | {finalAwardCount} award candidate{finalAwardCount !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      ) : (
        <div>
          <div className="section-subtitle">Paste your JSON payload below. A request_id will be auto-generated if left empty.</div>
          <textarea className="submit-editor" value={json} onChange={e => setJson(e.target.value)} spellCheck={false} />
          <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
            <button className="submit-btn" onClick={handleJsonSubmit} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Request'}
            </button>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Queued for async processing</span>
          </div>
        </div>
      )}

      {error && <div className="submit-error">Error: {error}</div>}
      {result && (
        <div className="submit-result">
          {result.requests ? (
            <>
              Created {result.requests.length} request(s):
              {result.requests.map(r => (
                <div key={r.request_id} style={{ marginTop: 4 }}>
                  <strong>{r.request_id}</strong> - {r.matching_mode?.toUpperCase()} | {r.expenditure_count} expenditures
                </div>
              ))}
              {result.group_id && <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-muted)' }}>Group: {result.group_id}</div>}
            </>
          ) : (
            <>Request <strong>{result.request_id}</strong> submitted. State: {result.state} | Mode: {result.matching_mode}</>
          )}
        </div>
      )}
    </div>
  );
}
